import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { quizzes } from '@/api/quizzes';
import type { QuizRow, CreateQuizBody } from '@/api/quizzes';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useEffectiveCustomerId } from '@/lib/customer-context-store';
import { useCustomerPicker } from '@/components/CustomerPicker';
import { CustomerScopeChip } from '@/components/CustomerScopeChip';
import { formatRef } from '@/lib/formatRef';
import { PageHeader } from '@/components/PageHeader';
import { DataTable } from '@/components/DataTable';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Plus, MoreHorizontal, Trash2, GraduationCap } from 'lucide-react';

const CAN_MUTATE = ['super_admin', 'reseller_owner', 'office_manager'];

export function QuizzesList() {
  const user = useCurrentUser();
  const effectiveCustomerId = useEffectiveCustomerId();
  const canMutate = CAN_MUTATE.includes(user.role);
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<QuizRow | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['quizzes', search, effectiveCustomerId],
    queryFn: () => quizzes.list({
      search: search.trim() || undefined,
      limit: 200,
      customer_id: effectiveCustomerId ?? undefined,
    }),
  });
  const rows = data?.rows ?? [];

  const remove = useMutation({
    mutationFn: (id: string) => quizzes.remove(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['quizzes'] }); setDeleteTarget(null); },
  });

  const columns = [
    { header: 'Ref', cell: (r: QuizRow) => <span className="font-mono text-xs text-slate-500">{formatRef(r.ref_number, 'QZ')}</span> },
    { header: 'Name', cell: (r: QuizRow) => (
      <Link to={`/quizzes/QZ-${r.ref_number}`} className="text-sm font-medium text-primary hover:underline">
        {r.name}
      </Link>
    ) },
    { header: 'Questions', cell: (r: QuizRow) => (
      <span className="font-mono text-xs tabular-nums">{r.question_count}</span>
    ) },
    { header: 'Pass threshold', cell: (r: QuizRow) => (
      <span className="text-xs text-slate-500">{r.pass_threshold_pct}%</span>
    ) },
    { header: 'Points on pass', cell: (r: QuizRow) => (
      <span className="font-mono text-xs tabular-nums">{r.points_on_pass}</span>
    ) },
    { header: 'Active', cell: (r: QuizRow) => (
      r.active ? <Badge variant="default">Active</Badge> : <Badge variant="secondary">Inactive</Badge>
    ) },
    { header: '', className: 'w-10 text-end', cell: (r: QuizRow) => (
      canMutate ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" aria-label="Row actions" onClick={(e) => e.stopPropagation()}
              className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
            <DropdownMenuItem asChild>
              <Link to={`/quizzes/QZ-${r.ref_number}`}>
                <GraduationCap className="h-3.5 w-3.5 mr-2" /> Open
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-red-600 focus:text-red-600 focus:bg-red-50"
              onClick={() => setDeleteTarget(r)}>
              <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null
    ) },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Engagement"
        title="Quizzes"
        description="DTMF-driven multiple-choice quizzes. Callers dial into an IVR quiz node and hear each question; the system scores their answers, grants points, and routes on pass or fail."
        actions={
          <div className="flex items-center gap-2">
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name..." className="h-9 w-56 text-sm" />
            {canMutate && (
              <Button size="sm" onClick={() => setCreateOpen(true)}>
                <Plus className="h-4 w-4 mr-1.5" /> New quiz
              </Button>
            )}
          </div>
        }
      />

      {isLoading ? (
        <div className="text-sm text-slate-400">Loading…</div>
      ) : (
        <DataTable rows={rows} columns={columns} empty={
          search ? `No quizzes match "${search}".` : 'No quizzes yet. Create one and add questions.'
        } />
      )}

      {createOpen && <CreateQuizDialog onClose={() => setCreateOpen(false)} />}
      {deleteTarget && (
        <Dialog open onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle>Delete quiz?</DialogTitle></DialogHeader>
            <p className="text-sm text-slate-600">
              <span className="font-medium">{deleteTarget.name}</span> ({formatRef(deleteTarget.ref_number, 'QZ')}) will
              be soft-deleted. Past attempts remain in your records.
            </p>
            <DialogFooter>
              <DialogClose asChild><Button type="button" variant="outline" disabled={remove.isPending}>Cancel</Button></DialogClose>
              <Button variant="destructive" disabled={remove.isPending} onClick={() => remove.mutate(deleteTarget.id)}>
                {remove.isPending ? 'Deleting…' : 'Delete'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

function CreateQuizDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [threshold, setThreshold] = useState(70);
  const [points, setPoints] = useState(0);
  const [adminEmail, setAdminEmail] = useState('');
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { effectiveCustomerId, element: customerPicker, isPickerVisible } =
    useCustomerPicker(customerId, setCustomerId, { required: true });

  const create = useMutation({
    mutationFn: (body: CreateQuizBody) => quizzes.create(body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['quizzes'] }); onClose(); },
    onError: (err) => setError(err instanceof Error ? err.message : 'Create failed.'),
  });

  function submit() {
    setError(null);
    if (!name.trim()) { setError('Name required.'); return; }
    if (effectiveCustomerId === null) { setError('Customer required.'); return; }
    create.mutate({
      name: name.trim(),
      description: description.trim() || null,
      pass_threshold_pct: threshold,
      points_on_pass: points,
      admin_email: adminEmail.trim() || null,
      ...(effectiveCustomerId
        ? { customer_id: effectiveCustomerId }
        : {}),
    });
  }

  const canSubmit = name.trim().length > 0 && effectiveCustomerId !== null;

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>New quiz</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          {customerPicker}
          <CustomerScopeChip />
          <div className="space-y-1">
            <Label htmlFor="qz-name">Name</Label>
            <Input id="qz-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Lesson 1 comprehension check" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="qz-desc">Description (optional)</Label>
            <Input id="qz-desc" value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="qz-threshold">Pass threshold (%)</Label>
              <Input id="qz-threshold" type="number" min={0} max={100} value={threshold} onChange={(e) => setThreshold(parseInt(e.target.value, 10) || 0)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="qz-points">Points on pass</Label>
              <Input id="qz-points" type="number" min={0} value={points} onChange={(e) => setPoints(parseInt(e.target.value, 10) || 0)} />
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="qz-admin-email">Admin email (optional)</Label>
            <Input id="qz-admin-email" type="email" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)}
              placeholder="notify@example.com" />
            <p className="text-xs text-muted-foreground">Notification on each attempt (feature pending).</p>
          </div>
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <DialogClose asChild><Button type="button" variant="outline" disabled={create.isPending}>Cancel</Button></DialogClose>
          <Button type="button" onClick={submit} disabled={create.isPending || !canSubmit}>
            {create.isPending ? 'Creating…' : 'Create quiz'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
